//React
import * as React from "react";
import { useState } from "react";
//Material UI
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
//Redux
import { useDispatch } from "react-redux";
import { showSuccess, showInfo, showError } from "../../../Global/notifications/notificationsSlice";
//RTK Query
import { useDeleteUserMutation, useSetUserActiveMutation } from "../../../Features/api/apiSlice";
//Components
import ConfirmDialog from "../ConfirmDialog";

/**
 * Row actions for the Inactive view.
 *
 * Reactivate is immediate and unconfirmed — it is not destructive and is
 * trivially undone. Delete permanently keeps its own confirm, and is the only
 * hard delete left in the UI; the design retains it solely for accounts
 * created by mistake.
 *
 * Inactive rows deliberately offer no edit: reactivate first, then edit, which
 * is the pattern ims/installers.html uses for the same soft-delete flow.
 */

const iconButtonSx = {
  width: 30,
  height: 30,
  borderRadius: "6px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "rgba(255, 255, 255, 0.5)",
  cursor: "pointer",
  border: "none",
  background: "transparent",
  "&:hover": { color: "var(--accent)", background: "rgba(253, 185, 64, 0.10)" },
  "& svg": { width: 15, height: 15 },
};

const dangerIconButtonSx = {
  ...iconButtonSx,
  "&:hover": { color: "#FCA5A5", background: "rgba(239, 68, 68, 0.10)" },
};

export default function InactiveRowActions({ user }) {
  const dispatch = useDispatch();
  const [setUserActive] = useSetUserActiveMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || "this user";

  const handleReactivate = async () => {
    try {
      await setUserActive({ body: { user_id: user.id, is_active: true } }).unwrap();
      dispatch(showSuccess(`${fullName} reactivated`));
    } catch (e) {
      dispatch(showError(`Could not reactivate ${fullName}`));
    }
  };

  const handleDelete = async () => {
    setConfirmOpen(false);
    try {
      await deleteUser({ body: { user_id: user.id } }).unwrap();
      dispatch(showInfo("User deleted"));
    } catch (e) {
      dispatch(showError(`Could not delete ${fullName}`));
    }
  };

  return (
    <>
      <Tooltip title="Reactivate" placement="top">
        <Box
          component="button"
          type="button"
          onClick={handleReactivate}
          aria-label={`Reactivate ${fullName}`}
          sx={iconButtonSx}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </Box>
      </Tooltip>

      <Tooltip title="Delete permanently" placement="top">
        <Box
          component="button"
          type="button"
          onClick={() => setConfirmOpen(true)}
          aria-label={`Delete ${fullName} permanently`}
          sx={dangerIconButtonSx}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
        </Box>
      </Tooltip>

      <ConfirmDialog
        open={confirmOpen}
        title={`Delete ${fullName} permanently?`}
        body="This removes their record entirely, including install history. Only do this if the account was created by mistake — a real user who's left should be deactivated instead, not deleted."
        cancelLabel="Cancel"
        confirmLabel="Delete permanently"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      />
    </>
  );
}
